import React, { useCallback, useEffect, useRef } from 'react';
import { lossMagnitudeLabel, signedPercentLabel } from './signedPercent';
import { beginOroborosLockSignIn } from './auth/startArtificeOAuth';
import { oroborosBrand } from './brand';
import { useOroborosLandingStats } from './hooks/useOroborosLandingStats';
import { useOroborosLandingLive, type LandingTapeRow } from './hooks/useOroborosLandingLive';
import { formatPct, type OroTopBacktestRow } from './api/strategies';
import { terminalNavHref } from './terminal/nav';
import './OroborosLanding.css';

function formatTapePrice(n: number | null): string {
  if (n == null || !Number.isFinite(n)) return '—';
  if (n >= 1000) return n.toLocaleString(undefined, { maximumFractionDigits: 0 });
  if (n >= 1) return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return n.toLocaleString(undefined, { maximumFractionDigits: 4 });
}

function formatTapeChange(n: number | null): string {
  if (n == null || !Number.isFinite(n)) return '';
  return `${signedPercentLabel(n, 2, true)}%`;
}

function pairLabel(pair: string): string {
  return String(pair || '').replace(/-(USD|USDC|PERP)$/i, '');
}

const TapeItem: React.FC<{ row: LandingTapeRow }> = ({ row }) => {
  const tone = row.changePct == null ? '' : row.changePct >= 0 ? ' oro-tape__chg--pos' : ' oro-tape__chg--neg';
  return (
    <span className="oro-tape__item">
      <span className="oro-tape__coin">{row.coin}</span>
      <span className="oro-tape__px">{formatTapePrice(row.mid)}</span>
      {row.changePct != null && <span className={`oro-tape__chg${tone}`}>{formatTapeChange(row.changePct)}</span>}
    </span>
  );
};

const Sparkline: React.FC<{ values?: number[]; positive: boolean }> = ({ values, positive }) => {
  if (!values || values.length < 2) return <span className="oro-proof__spark oro-proof__spark--empty" />;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const w = 100;
  const h = 28;
  const pts = values
    .map((v, i) => `${((i / (values.length - 1)) * w).toFixed(2)},${(h - ((v - min) / span) * (h - 2) - 1).toFixed(2)}`)
    .join(' ');
  return (
    <svg
      className={`oro-proof__spark${positive ? ' oro-proof__spark--pos' : ' oro-proof__spark--neg'}`}
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <polyline points={pts} fill="none" vectorEffect="non-scaling-stroke" />
    </svg>
  );
};

const ProofCard: React.FC<{ row: OroTopBacktestRow; href: string; index: number }> = ({ row, href, index }) => {
  const positive = row.totalReturn >= 0;
  return (
    <a className="oro-proof__card" href={href} style={{ animationDelay: `${0.06 * index}s` }}>
      <div className="oro-proof__head">
        <span className="oro-proof__rank">{String(index + 1).padStart(2, '0')}</span>
        <span className="oro-proof__name">{row.name}</span>
        <span className="oro-proof__market">
          {pairLabel(row.pair)} · {row.timeframe}
        </span>
      </div>
      <Sparkline values={row.sparkline} positive={positive} />
      <dl className="oro-proof__metrics">
        <div>
          <dt>Return</dt>
          <dd className={positive ? 'oro-proof__val--pos' : 'oro-proof__val--neg'}>{formatPct(row.totalReturn)}</dd>
        </div>
        <div>
          <dt>Win rate</dt>
          <dd>{signedPercentLabel(row.winRate, 0, false)}%</dd>
        </div>
        <div>
          <dt>Trades</dt>
          <dd>{row.totalTrades}</dd>
        </div>
        <div>
          <dt>Drawdown</dt>
          <dd className={row.maxDrawdown != null ? 'oro-proof__val--neg' : ''}>
            {row.maxDrawdown != null ? `-${lossMagnitudeLabel(row.maxDrawdown, 1)}%` : '—'}
          </dd>
        </div>
      </dl>
    </a>
  );
};

type FeatureId = 'autopilot' | 'discover' | 'control' | 'dashboard';

type Feature = {
  id: FeatureId;
  eyebrow: string;
  title: string;
  description: string;
  specs: string[];
  /** Terminal window title-bar label. */
  path: string;
};

const FEATURES: Feature[] = [
  {
    id: 'autopilot',
    eyebrow: 'Trader',
    title: 'Connect an account. Oroboros starts working.',
    description:
      'Pick Conservative, Balanced, or Aggressive. Set a mandate. Oroboros trades within those limits — you inspect when you want to.',
    specs: ['Pause, reduce risk, or take control', 'Standing instructions in plain language', 'No strategy catalog to operate first'],
    path: 'oroboros › home',
  },
  {
    id: 'discover',
    eyebrow: 'Ask',
    title: 'Cursor for trading.',
    description:
      'The primary control is a question. Why are we short ETH? Take less risk today. Find something asymmetric. Oroboros answers from the live book.',
    specs: ['Why this position', 'Change today’s risk', 'Hunt asymmetric setups'],
    path: 'oroboros › ask',
  },
  {
    id: 'control',
    eyebrow: 'Inspect',
    title: 'The terminal is the inspection layer.',
    description:
      'Charts, research, and the ticket are there when you need to see what the agent sees. Manual trading is the advanced mode.',
    specs: ['Activity, positions, research, charts', 'Strategies as contributing capabilities', 'Console for intervention'],
    path: 'oroboros › charts',
  },
  {
    id: 'dashboard',
    eyebrow: 'Now',
    title: 'What is my trader doing right now?',
    description:
      'Posture, open risk, last action, and what happens next — not a wall of strategy cards.',
    specs: ['Current posture and risk budget', 'Positions in R, not just dollars', 'Last action and next wait'],
    path: 'oroboros › home',
  },
];



const EQUITY_CURVE = [100, 101.2, 100.6, 102.4, 103.1, 102.7, 104.5, 105.2, 104.8, 106.6, 107.9, 107.4, 109.1, 110.3];

const TerminalWindow: React.FC<{ path: string; children: React.ReactNode }> = ({ path, children }) => (
  <div className="oro-tw" aria-hidden="true">
    <div className="oro-tw__bar">
      <span className="oro-tw__dots">
        <i />
        <i />
        <i />
      </span>
      <span className="oro-tw__path">{path}</span>
    </div>
    <div className="oro-tw__body">{children}</div>
  </div>
);

const AutopilotPanel: React.FC = () => (
  <>
    <div className="oro-tw__row oro-tw__row--head">
      <span className="oro-tw__live">
        <i className="oro-tw__pulse" /> Oroboros running
      </span>
      <span className="oro-tw__muted">BTC / ETH / SOL</span>
    </div>
    <ul className="oro-tw__list">
      <li className="oro-tw__row">
        <span className="oro-tw__name">
          BTC long
          <small>+1.8R · trailing</small>
        </span>
        <span className="oro-tw__pill oro-tw__pill--on">Held</span>
      </li>
      <li className="oro-tw__row">
        <span className="oro-tw__name">
          ETH short
          <small>+0.4R · holding</small>
        </span>
        <span className="oro-tw__pill oro-tw__pill--on">Held</span>
      </li>
      <li className="oro-tw__row">
        <span className="oro-tw__name">
          Risk engine
          <small>Blocking 2 proposed entries</small>
        </span>
        <span className="oro-tw__pill">Watch</span>
      </li>
    </ul>
    <div className="oro-tw__row oro-tw__row--foot">
      <span className="oro-tw__kv">
        <small>Posture</small>Defensive
      </span>
      <span className="oro-tw__kv">
        <small>Budget</small>34%
      </span>
      <span className="oro-tw__btn oro-tw__btn--warn">Pause</span>
    </div>
  </>
);

const AskPanel: React.FC = () => (
  <>
    <div className="oro-tw__row oro-tw__row--head">
      <span className="oro-tw__name">Ask Oroboros anything</span>
    </div>
    <ul className="oro-tw__list">
      <li className="oro-tw__row">
        <span className="oro-tw__muted">Why are we short ETH?</span>
      </li>
      <li className="oro-tw__row">
        <span className="oro-tw__muted">What would make you exit BTC?</span>
      </li>
      <li className="oro-tw__row">
        <span className="oro-tw__muted">Take less risk today.</span>
      </li>
    </ul>
    <div className="oro-tw__row oro-tw__row--foot">
      <span className="oro-tw__btn oro-tw__btn--accent">Ask</span>
    </div>
  </>
);

const DashboardPanel: React.FC<{ tape: LandingTapeRow[] }> = ({ tape }) => {
  const btc = tape.find((t) => t.coin === 'BTC');
  const eth = tape.find((t) => t.coin === 'ETH');
  return (
    <>
      <div className="oro-tw__row oro-tw__row--head">
        <span className="oro-tw__kv oro-tw__kv--big">
          <small>Equity</small>$24,860.42
        </span>
        <span className="oro-tw__kv">
          <small>Today</small>
          <span className="oro-tw__pos">+$412.60</span>
        </span>
        <span className="oro-tw__kv">
          <small>30d</small>
          <span className="oro-tw__pos">+10.3%</span>
        </span>
      </div>
      <div className="oro-tw__chart">
        <Sparkline values={EQUITY_CURVE} positive />
      </div>
      <ul className="oro-tw__list">
        <li className="oro-tw__row">
          <span className="oro-tw__name">
            <span className="oro-tw__pos">LONG</span> BTC
            <small>3× · mark {formatTapePrice(btc?.mid ?? 62480)}</small>
          </span>
          <span className="oro-tw__pos">+$286.10</span>
        </li>
        <li className="oro-tw__row">
          <span className="oro-tw__name">
            <span className="oro-tw__neg">SHORT</span> ETH
            <small>2× · mark {formatTapePrice(eth?.mid ?? 2506)}</small>
          </span>
          <span className="oro-tw__neg">−$41.20</span>
        </li>
      </ul>
    </>
  );
};

export const OroborosLanding: React.FC = () => {
  const { stats, mode } = useOroborosLandingStats();
  const { tape, tapeStatus, marketCount, strategies, strategiesStatus } = useOroborosLandingLive();
  const rootRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    const prevTitle = document.title;
    document.title = 'Oroboros — Autonomous trading agent';
    document.documentElement.classList.add('oro-page-active');
    document.body.classList.add('oro-page-active');
    return () => {
      document.title = prevTitle;
      document.documentElement.classList.remove('oro-page-active');
      document.body.classList.remove('oro-page-active');
    };
  }, []);

  useEffect(() => {
    const root = rootRef.current;
    const header = headerRef.current;
    const title = titleRef.current;
    if (!root || !header || !title) return;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    const apply = (brand: number, hero: number) => {
      root.style.setProperty('--oro-brand-word', String(brand));
      root.style.setProperty('--oro-hero-word', String(hero));
    };

    if (reduced.matches) {
      apply(1, 1);
      return;
    }

    let raf = 0;
    const update = () => {
      raf = 0;
      const titleBox = title.getBoundingClientRect();
      const headerBox = header.getBoundingClientRect();
      const start = headerBox.bottom + 28;
      const end = headerBox.bottom - titleBox.height * 0.25;
      const range = Math.max(64, start - end);
      const t = Math.min(1, Math.max(0, (start - titleBox.top) / range));
      apply(t, 1 - t);
    };
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(update);
    };

    root.addEventListener('scroll', onScroll, { passive: true });
    update();
    return () => {
      root.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  const goTerminal = useCallback(() => {
    window.location.href = oroborosBrand.terminalPath;
  }, []);

  const goSignIn = useCallback(() => {
    void beginOroborosLockSignIn();
  }, []);

  const strategiesHref = terminalNavHref(oroborosBrand.terminalPath, 'strategies');
  const strategyHref = (row: OroTopBacktestRow) =>
    `${strategiesHref}&strategyId=${encodeURIComponent(row.id)}&pair=${encodeURIComponent(
      row.pair,
    )}&timeframe=${encodeURIComponent(row.timeframe)}`;

  const tapeRows = tape.length ? [...tape, ...tape] : [];
  const statusLabel =
    tapeStatus === 'live' ? 'Live' : tapeStatus === 'loading' ? 'Connecting' : 'Offline';

  return (
    <div className="oro-root" ref={rootRef}>
      <div className="oro-hero-atmosphere" aria-hidden="true">
        <img
          className="oro-hero-atmosphere__plate"
          src={oroborosBrand.nebulaStartPath}
          srcSet={oroborosBrand.nebulaStartSrcSet}
          sizes="100vw"
          alt=""
          width={1024}
          height={1024}
          decoding="async"
        />
        <div className="oro-hero-atmosphere__veil" />
      </div>

      <div className="oro-shell">
        <header className="oro-header" ref={headerRef}>
          <a className="oro-brand" href={oroborosBrand.path} aria-label="Oroboros home">
            <span className="oro-brand__name">{oroborosBrand.name}</span>
          </a>

          <nav className="oro-nav" aria-label="Primary">
            <a href={oroborosBrand.docsUrl}>Docs</a>
          </nav>

          <div className="oro-header__actions">
            <button type="button" className="oro-link-sign" onClick={goSignIn}>
              Sign in
            </button>
          </div>
        </header>

        <div
          className={`oro-tape${tapeRows.length ? '' : ' oro-tape--empty'}`}
          aria-label="Perpetual market tape"
        >
          {tapeRows.length ? (
            <div className="oro-tape__track">
              {tapeRows.map((row, i) => (
                <TapeItem key={`${row.coin}-${i}`} row={row} />
              ))}
            </div>
          ) : (
            <span className="oro-tape__placeholder">
              {tapeStatus === 'offline' ? 'Market tape unavailable' : 'Connecting to markets…'}
            </span>
          )}
        </div>

        <section className="oro-hero" aria-label="Oroboros autonomous trading agent">
          <div className="oro-hero__visual" aria-hidden="true">
            <div className="oro-hero__radar">
              <span className="oro-hero__sweep" />
            </div>
            <div className="oro-hero__cross" />
            <img
              className="oro-hero__img"
              src={oroborosBrand.heroPath}
              srcSet={oroborosBrand.heroSrcSet}
              sizes="(max-width: 900px) 62vw, 420px"
              alt=""
              width={800}
              height={800}
              decoding="async"
            />
          </div>

          <div className="oro-hero__copy">
            <h1 className="oro-hero__title" ref={titleRef}>
              <span>{oroborosBrand.name}</span>
            </h1>
            <p className="oro-hero__job">
              An autonomous trader for <em>perpetuals</em> and <em>prop firms</em>.
            </p>
            <p className="oro-hero__tagline">It runs. You inspect. Ask it anything.</p>
          </div>

          <div className="oro-hero__act">
            <div className="oro-hero__ctas" aria-label="Primary actions">
              <button type="button" className="oro-btn oro-btn--solid" onClick={goTerminal}>
                Launch Oroboros
              </button>
            </div>
            <p className="oro-hero__signin">
              Have an account?{' '}
              <button type="button" className="oro-link-sign oro-link-sign--inline" onClick={goSignIn}>
                Sign in
              </button>
            </p>

            <p className="oro-hero__status" aria-live="polite">
              <span className={`oro-hero__dot oro-hero__dot--${tapeStatus}`} aria-hidden="true" />
              <span>{statusLabel}</span>
              {marketCount > 0 && (
                <>
                  <span className="oro-hero__status-sep">·</span>
                  <span>{marketCount} markets</span>
                </>
              )}
              {strategiesStatus === 'live' && strategies.length > 0 && (
                <>
                  <span className="oro-hero__status-sep">·</span>
                  <span>{strategies.length} ranked strategies</span>
                </>
              )}
            </p>
            <ul className="oro-hero__venues" aria-label="Trading venues">
              <li className="oro-hero__venue">
                <img
                  className="oro-hero__venue-logo"
                  src="/terminal/assets/venues/hyperliquid.svg"
                  alt=""
                  width={20}
                  height={20}
                  decoding="async"
                />
                <span className="oro-hero__venue-name">Hyperliquid</span>
                <span className="oro-hero__venue-badge">Live</span>
              </li>
              <li className="oro-hero__venue">
                <img
                  className="oro-hero__venue-logo"
                  src="/terminal/assets/venues/propr.svg"
                  alt=""
                  width={20}
                  height={20}
                  decoding="async"
                />
                <span className="oro-hero__venue-name">Propr</span>
                <span className="oro-hero__venue-badge oro-hero__venue-badge--next">Next</span>
              </li>
            </ul>

            <p className="oro-hero__legal">
              By continuing you agree to our{' '}
              <a href={oroborosBrand.termsUrl} target="_blank" rel="noopener noreferrer">
                Terms
              </a>{' '}
              and{' '}
              <a href={oroborosBrand.privacyUrl} target="_blank" rel="noopener noreferrer">
                Privacy Policy
              </a>
              .
            </p>
          </div>
        </section>

        {/* Three cards or nothing: a single ranked strategy reads as an empty shelf. */}
        {strategiesStatus !== 'offline' && (strategiesStatus === 'loading' || strategies.length >= 3) && (
          <section className="oro-proof" aria-label="Ranked strategies">
            <div className="oro-proof__header">
              <div>
            <h2 className="oro-proof__title">What it can trade</h2>
                <p className="oro-proof__subtitle">
                  Strategies contribute to decisions. You do not have to pick them to get value.
                </p>
              </div>
              <a className="oro-proof__more" href={strategiesHref}>
                Browse all →
              </a>
            </div>
            <div className="oro-proof__row">
              {strategiesStatus === 'loading'
                ? Array.from({ length: 4 }).map((_, i) => (
                    <div key={`skeleton-${i}`} className="oro-proof__card oro-proof__card--skeleton" aria-hidden="true" />
                  ))
                : strategies.map((row, i) => (
                    <ProofCard key={`${row.id}-${row.pair}-${row.timeframe}`} row={row} href={strategyHref(row)} index={i} />
                  ))}
            </div>
          </section>
        )}

        <section className="oro-features" aria-label="Product">
          <div className="oro-features__header">
            <h2 className="oro-features__title">Built as a trader, not a terminal</h2>
            <p className="oro-features__subtitle">
              Connect an account. Choose a stance. Oroboros works. Charts and tickets are there when
              you need to look.
            </p>
          </div>

          <div className="oro-features__rows">
            {FEATURES.map((feature, idx) => (
              <article
                key={feature.id}
                className={`oro-feature${idx % 2 ? ' oro-feature--flip' : ''}`}
                style={{ animationDelay: `${0.08 * idx}s` }}
              >
                <div className="oro-feature__info">
                  <p className="oro-feature__eyebrow">
                    <span>{String(idx + 1).padStart(2, '0')}</span> {feature.eyebrow}
                  </p>
                  <h3 className="oro-feature__title">{feature.title}</h3>
                  <p className="oro-feature__description">{feature.description}</p>
                  <ul className="oro-feature__specs">
                    {feature.specs.map((s) => (
                      <li key={s}>{s}</li>
                    ))}
                  </ul>
                </div>
                <div className="oro-feature__panel">
                  <TerminalWindow path={feature.path}>
                    {feature.id === 'autopilot' && <AutopilotPanel />}
                    {feature.id === 'discover' && <AskPanel />}
                    {feature.id === 'control' && <DashboardPanel tape={tape} />}
                    {feature.id === 'dashboard' && <AutopilotPanel />}
                  </TerminalWindow>
                </div>
              </article>
            ))}
          </div>

          <div className="oro-features__cta">
            <button type="button" className="oro-btn oro-btn--copper" onClick={goTerminal}>
              Start Oroboros
            </button>
          </div>
        </section>

        {mode === 'account' && (
          <section className="oro-stats" id="strategies" aria-label="Account metrics">
            {(stats.length
              ? stats
              : Array.from({ length: 5 }).map((_, i) => ({
                  value: '…',
                  label: 'Loading',
                  positive: undefined as boolean | undefined,
                }))
            ).map((stat, i) => (
              <div key={`${stat.label}-${i}`} className="oro-stat">
                <span
                  className={`oro-stat__value${
                    stat.positive === true
                      ? ' oro-stat__value--pos'
                      : stat.positive === false
                        ? ' oro-stat__value--neg'
                        : ''
                  }`}
                >
                  {stat.value}
                </span>
                <span className="oro-stat__label">{stat.label}</span>
              </div>
            ))}
          </section>
        )}

        <footer className="oro-footer" role="contentinfo">
          <div className="oro-footer__inner">
            <a
              className="oro-footer__jester"
              href={oroborosBrand.jesterSiteUrl}
              target="_blank"
              rel="noopener noreferrer"
              title="Jester"
              aria-label="Jester — www.jester.trade"
            >
              <img
                src={oroborosBrand.jesterMarkPath}
                alt=""
                width={22}
                height={22}
                aria-hidden="true"
              />
              <span>Jester</span>
            </a>

            <div className="oro-footer__legal">
              <p className="oro-footer__copy">
                © {new Date().getFullYear()} {oroborosBrand.name}
              </p>
            </div>

            <nav className="oro-footer__links" aria-label="Oroboros links">
              <a href={oroborosBrand.termsUrl} target="_blank" rel="noopener noreferrer">
                Terms
              </a>
              <a href={oroborosBrand.privacyUrl} target="_blank" rel="noopener noreferrer">
                Privacy
              </a>
            </nav>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default OroborosLanding;
